import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Mail, Phone } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  AuthLayout,
  FormError,
  GoogleIcon,
  OrDivider,
  PasswordInput,
  startGoogleSignIn,
} from '../components/AuthLayout';
import { isGoogleAuthAvailable } from '../utils/googleAuth';
import { getErrorMessage } from '../utils/errors';
import { getPostAuthPath, rememberAfterVerifyPath } from '../utils/redirect';
import { useDocumentTitle } from '../hooks/use-document-title';
import CheckEmailNotice from '../components/CheckEmailNotice';

export default function LoginPage() {
  useDocumentTitle('Sign in');
  const navigate = useNavigate();
  const location = useLocation();
  const { user, login, loginPhone } = useAuth();

  const [method, setMethod] = useState('email');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [unverifiedEmail, setUnverifiedEmail] = useState('');

  const googleAvailable = isGoogleAuthAvailable();

  // Already signed in (e.g. navigated back to /login) — skip the form.
  useEffect(() => {
    if (user) navigate(getPostAuthPath(location.state, user), { replace: true });
  }, [user, navigate, location.state]);

  const finish = (signedInUser) => {
    toast.success(`Welcome back${signedInUser?.name ? `, ${signedInUser.name.split(' ')[0]}` : ''}!`);
    navigate(getPostAuthPath(location.state, signedInUser), { replace: true });
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      finish(await login(email.trim(), password));
    } catch (err) {
      // 403 only comes back once the password matched: the account just
      // hasn't confirmed its email yet.
      if (err.response?.status === 403) {
        rememberAfterVerifyPath(location.state);
        setUnverifiedEmail(email.trim());
        return;
      }
      setError(getErrorMessage(err, 'Incorrect email or password.'));
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const digits = phone.replace(/\D/g, '');
    if (digits.length < 10) {
      setError('Please enter a valid 10-digit phone number.');
      return;
    }
    if (!/^\d{4}$/.test(pin)) {
      setError('Your PIN is the 4-digit code you set up over the phone.');
      return;
    }
    setLoading(true);
    try {
      finish(await loginPhone(phone.trim(), pin));
    } catch (err) {
      setError(getErrorMessage(err, 'Incorrect phone number or PIN.'));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    if (!startGoogleSignIn()) {
      toast.error('Google sign-in is temporarily unavailable. Please sign in with your email instead.');
    }
  };

  if (unverifiedEmail) {
    return (
      <AuthLayout
        title="Confirm your email first"
        footer={
          <button type="button" onClick={() => setUnverifiedEmail('')} className="text-gold hover:underline font-medium">
            Back to sign in
          </button>
        }
      >
        <CheckEmailNotice email={unverifiedEmail} intro="When you signed up we sent a link to" />
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Welcome back"
      subtitle="Sign in to continue creating beautiful invitations"
      footer={
        <>
          Don't have an account?{' '}
          <Link to="/register" state={location.state} className="text-gold hover:underline font-medium">
            Create one
          </Link>
        </>
      }
    >
      {googleAvailable && (
        <>
          <Button
            type="button"
            variant="outline"
            onClick={handleGoogleLogin}
            className="w-full h-10 [&_svg]:size-5"
          >
            <GoogleIcon />
            Continue with Google
          </Button>
          <OrDivider label="or sign in with" />
        </>
      )}

      <Tabs
        value={method}
        onValueChange={(value) => {
          setMethod(value);
          setError('');
        }}
      >
        <TabsList className="grid grid-cols-2 w-full mb-6 bg-cream">
          <TabsTrigger value="email" data-testid="login-tab-email">
            <Mail className="h-4 w-4 mr-1.5" aria-hidden="true" />
            Email
          </TabsTrigger>
          <TabsTrigger value="phone" data-testid="login-tab-phone">
            <Phone className="h-4 w-4 mr-1.5" aria-hidden="true" />
            Phone &amp; PIN
          </TabsTrigger>
        </TabsList>

        <TabsContent value="email" className="mt-0">
          <form onSubmit={handleEmailSubmit} className="space-y-5">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                className="mt-2"
                aria-invalid={Boolean(error) || undefined}
                data-testid="login-email"
              />
            </div>

            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link to="/forgot-password" className="text-sm text-ink/70 hover:text-ink hover:underline">
                  Forgot password?
                </Link>
              </div>
              <PasswordInput
                id="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Your password"
                required
                className="mt-2"
                aria-invalid={Boolean(error) || undefined}
                data-testid="login-password"
              />
            </div>

            <FormError message={method === 'email' ? error : ''} />

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-10 bg-gold text-white hover:bg-gold/90"
              data-testid="login-submit"
            >
              {loading ? 'Signing in…' : 'Sign In'}
            </Button>
          </form>
        </TabsContent>

        <TabsContent value="phone" className="mt-0">
          <form onSubmit={handlePhoneSubmit} className="space-y-5">
            <p className="text-sm text-ink/60">
              Ordered by phone? Sign in with your phone number and the 4-digit web PIN you set up on our phone line.
            </p>
            <div>
              <Label htmlFor="phone">Phone number</Label>
              <Input
                id="phone"
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="(845) 555-0123"
                required
                className="mt-2"
                data-testid="login-phone"
              />
            </div>
            <div>
              <Label htmlFor="pin">4-digit PIN</Label>
              <Input
                id="pin"
                type="password"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={4}
                pattern="\d{4}"
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                placeholder="••••"
                required
                className="mt-2 tracking-[0.5em]"
                data-testid="login-pin"
              />
            </div>

            <FormError message={method === 'phone' ? error : ''} />

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-10 bg-gold text-white hover:bg-gold/90"
              data-testid="login-phone-submit"
            >
              {loading ? 'Signing in…' : 'Sign In'}
            </Button>
          </form>
        </TabsContent>
      </Tabs>
    </AuthLayout>
  );
}
